import { JobType, JobTypeConfig, JOB_TYPE_CONFIGS, StepType } from '../graphql/types';
import type { TitleSearchStep } from '../titleSearch/steps';

/**
 * JobTypeConfigService provides helper functions to access job-type-specific
 * configuration values for labels, descriptions, and display names.
 * 
 * Requirements: 1.5, 14.4
 */

/**
 * Returns the full configuration for a given job type.
 * @param jobType - The job type (DD or BA)
 * @returns The JobTypeConfig for the specified job type
 */
export function getConfig(jobType: JobType): JobTypeConfig {
  return JOB_TYPE_CONFIGS[jobType];
}

/**
 * Returns the label for a specific step within a job type.
 * @param jobType - The job type (DD or BA)
 * @param step - The step type
 * @returns The display label for the step
 */
export function getStepLabel(jobType: JobType, step: StepType): string {
  const config = JOB_TYPE_CONFIGS[jobType];
  
  switch (step) {
    case StepType.DOCUMENT_PROCESSING:
      return config.stepLabels.documentProcessing;
    case StepType.DOCUMENT_VERIFICATION:
      return config.stepLabels.documentVerification;
    case StepType.INVESTOR_GUIDELINE_REVIEW:
      return config.stepLabels.investorGuidelineReview;
    case StepType.FINDINGS_DISPLAY:
      return config.titleSearchStepLabels?.findingsDisplay ?? 'Findings';
    default:
      return '';
  }
}

/**
 * Returns the display name for a job type.
 * @param jobType - The job type (DD or BA)
 * @returns The human-readable display name
 */
export function getDisplayName(jobType: JobType): string {
  return JOB_TYPE_CONFIGS[jobType].displayName;
}

/**
 * Returns the label for a Title Search (TR) step.
 *
 * The title-search workflow has its own four ordered steps that differ from the
 * shared DD/BA three-step model, so they are looked up from the config's
 * `titleSearchStepLabels`. Falls back to a humanized version of the step key if
 * the labels are absent (which should not happen for a correctly configured TR
 * job type).
 *
 * Requirements: 8.1, 8.4
 */
export function getTitleSearchStepLabel(step: TitleSearchStep): string {
  const labels = JOB_TYPE_CONFIGS[JobType.TR].titleSearchStepLabels;
  if (labels) {
    switch (step) {
      case 'property_tax_search':
        return labels.propertyTaxSearch;
      case 'recorded_document_search':
        return labels.recordedDocumentSearch;
      case 'document_processing':
        return labels.documentProcessing;
      case 'findings_display':
        return labels.findingsDisplay;
    }
  }
  // Fallback: humanize the step key.
  return step
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
